import { AlertTriangle, Calendar, DollarSign, TrendingUp } from "lucide-react";
import { DateTime } from "luxon";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { CustomerAnalyticsQuery } from "@/gql/graphql";
import { timezoneUtils } from "@/lib/timezone";
import { formatCurrency } from "../../utils/customer-utils";

interface CustomerEssentialMetricsCardProps {
  analytics?: NonNullable<CustomerAnalyticsQuery["customerAnalytics"]>;
  loading?: boolean;
}

export function CustomerEssentialMetricsCard({
  analytics,
  loading = false,
}: CustomerEssentialMetricsCardProps) {
  if (loading || !analytics) {
    return <EssentialMetricsSkeleton />;
  }

  const { lifetimeValue, visitFrequency, churnRisk } = analytics;

  const getRiskColor = (level: string) => {
    switch (level) {
      case "CRITICAL":
        return "bg-red-100 text-red-900 border-red-300 dark:bg-red-950 dark:text-red-100 dark:border-red-800";
      case "HIGH":
        return "bg-orange-100 text-orange-900 border-orange-300 dark:bg-orange-950 dark:text-orange-100 dark:border-orange-800";
      case "MEDIUM":
        return "bg-yellow-100 text-yellow-900 border-yellow-300 dark:bg-yellow-950 dark:text-yellow-100 dark:border-yellow-800";
      case "LOW":
        return "bg-green-100 text-green-900 border-green-300 dark:bg-green-950 dark:text-green-100 dark:border-green-800";
      case "NEW_CUSTOMER":
        return "bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-950 dark:text-blue-100 dark:border-blue-800";
      default:
        return "bg-gray-100 text-gray-900 border-gray-300 dark:bg-gray-950 dark:text-gray-100 dark:border-gray-800";
    }
  };

  const getTrendIcon = (_direction: string, percent?: number | null) => {
    if (!percent) return null;

    const isPositive = percent > 0;
    return (
      <div
        className={`flex items-center gap-1 text-sm ${
          isPositive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
        }`}
      >
        <TrendingUp className={`w-3 h-3 ${!isPositive ? "rotate-180" : ""}`} />
        <span>
          {isPositive ? "+" : ""}
          {percent.toFixed(1)}%
        </span>
      </div>
    );
  };

  const formatFrequency = (days: number) => {
    if (days < 7) return `Every ${days} day${days !== 1 ? "s" : ""}`;
    if (days < 28)
      return `Every ${Math.round(days / 7)} week${Math.round(days / 7) !== 1 ? "s" : ""}`;
    return `Every ${Math.round(days / 30)} month${Math.round(days / 30) !== 1 ? "s" : ""}`;
  };

  const formatDaysAgo = (days: number) => {
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days < 7) return `${days} days ago`;
    if (days < 30)
      return `${Math.round(days / 7)} week${Math.round(days / 7) !== 1 ? "s" : ""} ago`;
    return `${Math.round(days / 30)} month${Math.round(days / 30) !== 1 ? "s" : ""} ago`;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          Essential Metrics
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Customer Lifetime Value */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 p-4 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Customer Lifetime Value
              </span>
            </div>
            {getTrendIcon(lifetimeValue.spendingTrendDirection, lifetimeValue.spendingTrendPercent)}
          </div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {formatCurrency(lifetimeValue.lifetimeValue)}
          </div>
          <div className="text-xs text-blue-700 dark:text-blue-300 mt-1">
            From {lifetimeValue.totalVisits} visit{lifetimeValue.totalVisits !== 1 ? "s" : ""} •
            Avg: {formatCurrency(lifetimeValue.avgSpendPerVisit)}
          </div>
        </div>

        {/* Visit Pattern & Last Visit Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-muted p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Visit Pattern</span>
            </div>
            <div className="text-sm font-semibold">
              {formatFrequency(visitFrequency.avgDaysBetweenVisits)}
            </div>
            <div className="text-xs text-muted-foreground capitalize">
              {visitFrequency.visitConsistency.replace("_", " ")}
            </div>
          </div>

          <div className="bg-muted p-3 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground">Last Visit</span>
            </div>
            <div className="text-sm font-semibold">
              {formatDaysAgo(visitFrequency.daysSinceLastVisit)}
            </div>
            {lifetimeValue.lastVisitDate && (
              <div className="text-xs text-muted-foreground">
                {timezoneUtils
                  .fromISO(lifetimeValue.lastVisitDate)
                  .toLocaleString(DateTime.DATE_MED)}
              </div>
            )}
          </div>
        </div>

        {/* Churn Risk */}
        <div className="flex items-center justify-between p-3 border rounded-lg">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">Churn Risk</span>
          </div>
          <Badge variant="outline" className={getRiskColor(churnRisk.churnRiskLevel)}>
            {churnRisk.churnRiskLevel === "NEW_CUSTOMER"
              ? "New Customer"
              : churnRisk.churnRiskLevel.toLowerCase()}
          </Badge>
        </div>

        {/* Risk Action (only if medium or higher risk) */}
        {["CRITICAL", "HIGH", "MEDIUM"].includes(churnRisk.churnRiskLevel) && (
          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-3 rounded-lg">
            <div className="text-xs font-medium text-amber-900 dark:text-amber-100 mb-1">
              Recommended Action
            </div>
            <div className="text-sm text-amber-800 dark:text-amber-200">
              {churnRisk.recommendedAction}
            </div>
          </div>
        )}

        {/* Cache freshness indicator */}
        <div className="flex items-center justify-center pt-2 border-t">
          <div className="text-xs text-muted-foreground">
            Updated {timezoneUtils.fromISO(analytics.lastUpdated).toRelative()}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EssentialMetricsSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      </CardHeader>
      <CardContent className="space-y-4">
        {/* CLV Skeleton */}
        <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-1" />
          <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-2/3" />
        </div>

        {/* Grid Skeleton */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-1" />
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-1/2" />
          </div>
          <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-1" />
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-2/3" />
          </div>
        </div>

        {/* Risk Skeleton */}
        <div className="h-12 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
      </CardContent>
    </Card>
  );
}
